import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { authStore } from '@/lib/authStore';
import { ApiError } from '@/lib/apiError';
import { handleAuthFailure, shouldTreatAsAuthFailure, tryRefreshBeforeLogout } from '@/lib/sessionGuard';
import type { ExtendedUser } from '@/lib/oidcConfig';
import { deriveTenantScope } from '@/lib/tenantScope';
import { resolveApiUrl } from '@/lib/apiBase';

const extractDetail = (
  payload: unknown,
): { detail?: string; code?: string; type?: string; details?: unknown; messageKey?: string } => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const data = payload as Record<string, unknown>;
  const detailCandidate = [
    data.detail,
    data.message,
    data.error,
    data.title,
    data.description,
  ].find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;

  const codeCandidate = [
    data.code,
    data.error,
    data.detail_code,
    data.reason,
  ].find((value) => typeof value === 'string' && value.trim().length > 0) as string | undefined;

  const rawType = typeof data.type === 'string' ? data.type.trim() : '';
  const typeCandidate = rawType.length > 0 ? rawType : undefined;

  const detailsCandidate =
    data.details !== undefined && data.details !== null ? data.details : undefined;

  const messageKeyCandidate =
    typeof data.messageKey === 'string' && data.messageKey.trim().length > 0
      ? data.messageKey.trim()
      : undefined;

  return {
    detail: detailCandidate?.trim(),
    code: codeCandidate?.trim(),
    type: typeCandidate,
    details: detailsCandidate,
    messageKey: messageKeyCandidate,
  };
};

async function ensureOk(res: Response): Promise<void> {
  if (res.ok) {
    return;
  }
  if (shouldTreatAsAuthFailure(res.status)) {
    handleAuthFailure(res.status);
  }

  let bodyText = '';
  let payload: unknown;
  try {
    bodyText = await res.text();
    if (bodyText) {
      payload = JSON.parse(bodyText);
    }
  } catch {
    payload = bodyText || undefined;
  }

  const { detail, code, type, details, messageKey } = extractDetail(payload);
  const message =
    detail ||
    (typeof bodyText === 'string' && bodyText.trim().length > 0 ? bodyText.trim() : '') ||
    res.statusText ||
    `Request failed with status ${res.status}`;

  throw new ApiError(res.status, message, {
    status: res.status,
    code,
    type,
    detail: message,
    details,
    payload,
    messageKey,
  });
}

const devTokenFallback = () => {
  const authMode = (import.meta.env.VITE_AUTH_MODE ?? '').toString().trim().toLowerCase();
  return authMode === 'demo' ? undefined : import.meta.env.VITE_DEV_TOKEN;
};

function buildHeaders(data?: unknown): Record<string, string> {
  const token = authStore.getToken?.() || devTokenFallback();
  const user = (authStore.getUser?.() as ExtendedUser | null) || null;
  const scope = deriveTenantScope(user);

  const headers: Record<string, string> = {};
  if (data) headers['Content-Type'] = 'application/json';
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (scope.platformSlug) {
    headers['x-tenant-id'] = scope.platformSlug;
  }
  if (scope.platformUuid) {
    headers['x-tenant-uuid'] = scope.platformUuid;
  }
  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const fullUrl = resolveApiUrl(url);
  const headers = { ...buildHeaders(data), ...(extraHeaders ?? {}) };

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // On 401, attempt a single token refresh + retry
  if (res.status === 401) {
    const refreshed = await tryRefreshBeforeLogout(401);
    if (refreshed) {
      const retryRes = await fetch(fullUrl, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: "include",
      });
      await ensureOk(retryRes);
      return retryRes;
    }
  }

  await ensureOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const urlPart = queryKey[0] as string;
    const fullUrl = resolveApiUrl(urlPart);

    const doFetch = () =>
      fetch(fullUrl, {
        headers: buildHeaders(),
        credentials: "include",
      });

    let res = await doFetch();

    // On 401, attempt a single token refresh + retry
    if (res.status === 401) {
      const refreshed = await tryRefreshBeforeLogout(401);
      if (refreshed) {
        res = await doFetch();
      }
    }

    if (shouldTreatAsAuthFailure(res.status)) {
      handleAuthFailure(res.status);
    }

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await ensureOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
